// Standalone notification preferences card backed by useUserPreference.
// Mirrors NotificationSettingsSection but does not require a parent
// react-hook-form context, so it can live on the standalone /preferences page.
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell } from "lucide-react";
import { useUserPreference } from "@/hooks/useUserPreference";

const isBool = (v: unknown): v is boolean => typeof v === "boolean";

function ToggleRow({
  prefKey,
  defaultValue,
  label,
  description,
  localStorageKey,
}: {
  prefKey: string;
  defaultValue: boolean;
  label: string;
  description: string;
  localStorageKey: string;
}) {
  const { value, setValue, isSaving } = useUserPreference<boolean>({
    key: prefKey,
    defaultValue,
    validate: isBool,
    localStorageKey,
  });
  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="space-y-0.5">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch checked={value} onCheckedChange={setValue} disabled={isSaving} />
    </div>
  );
}

export function NotificationPreferencesSection() {
  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
        </GlassCardTitle>
        <GlassCardDescription>
          Choose how and when you want to be notified.
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className="space-y-4">
        <ToggleRow
          prefKey="notifications.email"
          defaultValue={true}
          label="Email Notifications"
          description="Receive email notifications for important events"
          localStorageKey="pref:notifications.email"
        />
        <ToggleRow
          prefKey="notifications.browser"
          defaultValue={true}
          label="Browser Notifications"
          description="Show browser notifications for real-time updates"
          localStorageKey="pref:notifications.browser"
        />
        <ToggleRow
          prefKey="notifications.systemAlerts"
          defaultValue={true}
          label="System Alerts"
          description="Get alerts for system issues and maintenance"
          localStorageKey="pref:notifications.systemAlerts"
        />
      </GlassCardContent>
    </GlassCard>
  );
}
